import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  CheckCircle2,
  Loader2,
  Send,
  Mail,
  Truck as TruckIcon,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { LINKS } from "@/config/links";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = {
  name: "",
  company: "",
  email: "",
  phone: "",
  dot_number: "",
  power_units: "",
  current_software: "",
  books_current: "",
  preferred_plan: "",
  comments: "",
};

export default function Inquiry() {
  const [params] = useSearchParams();
  const isCustom = params.get("custom") === "true";

  const [form, setForm] = useState({
    ...initial,
    preferred_plan: isCustom ? "custom" : "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  const setField = (name, val) => setForm((f) => ({ ...f, [name]: val }));

  const requiredOk =
    form.name &&
    form.email &&
    form.phone &&
    form.power_units &&
    form.current_software &&
    form.books_current &&
    form.preferred_plan;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!requiredOk) {
      toast.error("Please fill out all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        company: form.company || undefined,
        email: form.email,
        phone: form.phone,
        dot_number: form.dot_number || undefined,
        power_units: form.power_units,
        current_software: form.current_software,
        books_current: form.books_current,
        preferred_plan: form.preferred_plan,
        comments: form.comments || undefined,
      };
      const { data } = await axios.post(`${API}/inquiry`, payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (result) return <ConfirmationView result={result} />;

  return (
    <div data-testid="page-inquiry" className="min-h-screen pb-24">
      <Toaster theme="dark" position="top-right" richColors />

      <section className="relative pt-12 sm:pt-16 pb-8 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[420px] gradient-radial-blue pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <Link
            to="/services"
            data-testid="inquiry-back-link"
            className="inline-flex items-center gap-2 text-sm text-[#9CA3AF] hover:text-white transition-colors"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Services
          </Link>

          <div className="mt-8">
            <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Inquiry (optional)</p>
            <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-[1.1]">
              Tell us about your operation.
            </h1>
            <p className="mt-4 text-[#9CA3AF] leading-relaxed">
              Optional. The primary path is the 30-minute call. If you’d rather write it out first, this takes about two minutes and we’ll reply to book the call.
            </p>
          </div>
        </div>
      </section>

      <section className="relative">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <form onSubmit={onSubmit} data-testid="inquiry-form" className="space-y-8">
            {/* Contact */}
            <FormBlock title="Contact information">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Full name" name="name" value={form.name} onChange={onChange} required testid="inquiry-input-name" />
                <Field label="Company (optional)" name="company" value={form.company} onChange={onChange} testid="inquiry-input-company" />
                <Field label="Email" type="email" name="email" value={form.email} onChange={onChange} required testid="inquiry-input-email" />
                <Field label="Phone" name="phone" value={form.phone} onChange={onChange} required testid="inquiry-input-phone" />
              </div>
            </FormBlock>

            {/* Operation */}
            <FormBlock title="Your operation">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="DOT number (optional)" name="dot_number" value={form.dot_number} onChange={onChange} testid="inquiry-input-dot" />
                <SelectField
                  label="Number of power units"
                  name="power_units"
                  value={form.power_units}
                  onChange={onChange}
                  required
                  testid="inquiry-input-power-units"
                  options={["1", "2", "3", "4", "5", "6-10", "11-25", "25+"]}
                />
                <SelectField
                  label="Current bookkeeping software"
                  name="current_software"
                  value={form.current_software}
                  onChange={onChange}
                  required
                  testid="inquiry-input-software"
                  options={[
                    "QuickBooks Online",
                    "QuickBooks Desktop",
                    "Xero",
                    "Wave",
                    "Spreadsheets",
                    "Nothing formal",
                    "Other",
                  ]}
                />
                <ChoiceField
                  label="Are your books current?"
                  name="books_current"
                  value={form.books_current}
                  onChange={(v) => setField("books_current", v)}
                  required
                  testidPrefix="inquiry-books-current"
                  options={[
                    { value: "yes", label: "Yes" },
                    { value: "no", label: "No" },
                    { value: "unsure", label: "Not sure" },
                  ]}
                />
              </div>
            </FormBlock>

            {/* Preferred Plan */}
            <FormBlock title="Preferred plan">
              <ChoiceField
                label="Which plan interests you most?"
                name="preferred_plan"
                value={form.preferred_plan}
                onChange={(v) => setField("preferred_plan", v)}
                required
                testidPrefix="inquiry-plan"
                columns={3}
                options={[
                  { value: "truck1_mtm", label: "1 truck · month-to-month · $499/mo" },
                  { value: "truck1_6mo", label: "1 truck · 6-month · $399/mo" },
                  { value: "truck1_12mo", label: "1 truck · 12-month · $349/mo" },
                  { value: "fleet_mtm", label: "2–5 trucks · month-to-month · $649/mo" },
                  { value: "fleet_6mo", label: "2–5 trucks · 6-month · $499/mo" },
                  { value: "fleet_12mo", label: "2–5 trucks · 12-month · $449/mo" },
                  { value: "custom", label: "Custom Engagement" },
                ]}
              />
            </FormBlock>

            {/* Comments */}
            <FormBlock title="Anything else?">
              <label className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#9CA3AF] mb-2">
                Additional comments (optional)
              </label>
              <textarea
                name="comments"
                value={form.comments}
                onChange={onChange}
                rows={4}
                data-testid="inquiry-input-comments"
                placeholder="Anything specific about your operation, deadlines, or questions you’d like us to answer on the Discovery Call."
                className="w-full rounded-xl bg-[#040914] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#FFCB05]/50 focus:ring-2 focus:ring-[#FFCB05]/20 transition-colors"
              />
            </FormBlock>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <p className="text-xs text-[#6B7280]">Submitting doesn’t commit you to anything &mdash; we’ll review and reach out to schedule a call.</p>
              <button
                type="submit"
                disabled={submitting}
                data-testid="inquiry-submit-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] disabled:opacity-60 disabled:cursor-not-allowed transition-all hover:-translate-y-0.5"
              >
                {submitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Submitting&hellip;</>) : (<>Submit Inquiry <Send className="h-4 w-4" /></>)}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

// ============ CONFIRMATION ============
function ConfirmationView({ result }) {
  const firstName = result?.name?.split(" ")[0] || "friend";
  return (
    <div data-testid="page-inquiry-confirmation" className="min-h-screen pb-24">
      <section className="relative pt-16 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[420px] gradient-radial-blue pointer-events-none" />
        <div className="relative max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FFCB05]/30 bg-[#FFCB05]/5">
              <CheckCircle2 className="h-4 w-4 text-[#FFCB05]" />
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#FFCB05]">Inquiry received</span>
            </div>

            <h1 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white leading-[1.05]">
              Thanks, {firstName}. <span className="text-[#FFCB05]">We’ve got it.</span>
            </h1>

            <p className="mt-5 text-[#9CA3AF] leading-relaxed">
              We’ll review your information and contact you to schedule a Discovery Call. Expect to hear from us within one business day.
            </p>

            <div className="mt-8 rounded-2xl border border-white/5 bg-[#0A111E] p-6">
              <p className="text-xs font-semibold tracking-[0.18em] uppercase text-[#9CA3AF]">What happens next</p>
              <ol className="mt-4 space-y-4 text-sm text-[#E5E7EB]">
                <li className="flex items-start gap-3">
                  <span className="inline-grid place-items-center h-6 w-6 rounded-full bg-[#FFCB05] text-[#040914] text-xs font-semibold shrink-0">1</span>
                  <span>We review your inquiry and reach out to book the 30-minute call.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-grid place-items-center h-6 w-6 rounded-full bg-[#FFCB05] text-[#040914] text-xs font-semibold shrink-0">2</span>
                  <span>On the call, we cover fit, trucks, ELD, whether books exist, and which term makes sense.</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="inline-grid place-items-center h-6 w-6 rounded-full bg-[#FFCB05] text-[#040914] text-xs font-semibold shrink-0">3</span>
                  <span>If we’re a fit, engagement letter goes out. Sign and pay, and we start.</span>
                </li>
              </ol>
            </div>

            <div className="mt-8 rounded-2xl border border-[#FFCB05]/30 bg-gradient-to-br from-[#00274C] to-[#040914] p-6 sm:p-8">
              <div className="flex items-start gap-4">
                <span className="inline-grid place-items-center h-11 w-11 rounded-xl bg-[#040914] border border-[#FFCB05]/40 shrink-0">
                  <Calendar className="h-5 w-5 text-[#FFCB05]" />
                </span>
                <div>
                  <p className="font-display text-lg font-semibold text-white">Skip the wait &mdash; grab a spot.</p>
                  <p className="mt-1 text-sm text-[#9CA3AF]">Book the 30-minute call directly on our calendar.</p>
                  <a
                    href={LINKS.discoveryCall}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="confirmation-calendly-btn"
                    className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-[#FFCB05] text-[#040914] hover:bg-[#FFD633] transition-colors"
                  >
                    See if Linehaul is a fit (30 min) <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/"
                data-testid="confirmation-home-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-white/15 text-white hover:border-[#FFCB05]/50 hover:bg-white/5 transition-all"
              >
                Back to Home
              </Link>
              <Link
                to="/logbook"
                data-testid="confirmation-logbook-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold border border-white/15 text-white hover:border-[#FFCB05]/50 hover:bg-white/5 transition-all"
              >
                Read The Logbook
              </Link>
            </div>

            <p className="mt-8 text-xs text-[#6B7280] inline-flex items-center gap-2">
              <Mail className="h-3.5 w-3.5" /> Confirmation sent internally &mdash; reference #{result.id.slice(0, 8)}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

// ============ small inputs ============
function FormBlock({ title, children }) {
  return (
    <div className="p-6 sm:p-8 rounded-2xl border border-white/5 bg-gradient-to-b from-[#00274C]/30 to-[#040914]">
      <h2 className="font-display text-lg font-semibold text-white mb-5 flex items-center gap-2">
        <TruckIcon className="h-4 w-4 text-[#FFCB05]" /> {title}
      </h2>
      {children}
    </div>
  );
}

function Field({ label, name, value, onChange, type = "text", required = false, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#9CA3AF] mb-2">
        {label}{required && <span className="text-[#FFCB05]"> *</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full rounded-xl bg-[#040914] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-[#6B7280] focus:outline-none focus:border-[#FFCB05]/50 focus:ring-2 focus:ring-[#FFCB05]/20 transition-colors"
      />
    </div>
  );
}

function SelectField({ label, name, value, onChange, options, required = false, testid }) {
  return (
    <div>
      <label className="block text-xs font-semibold tracking-[0.12em] uppercase text-[#9CA3AF] mb-2">
        {label}{required && <span className="text-[#FFCB05]"> *</span>}
      </label>
      <select
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full rounded-xl bg-[#040914] border border-white/10 px-4 py-3 text-sm text-white focus:outline-none focus:border-[#FFCB05]/50 focus:ring-2 focus:ring-[#FFCB05]/20 transition-colors"
      >
        <option value="">Select&hellip;</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function ChoiceField({ label, value, onChange, options, required = false, testidPrefix, columns = 3 }) {
  const gridClass = columns === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2";
  return (
    <div className="sm:col-span-2">
      <label className="block text-sm font-medium text-white mb-3">
        {label}{required && <span className="text-[#FFCB05]"> *</span>}
      </label>
      <div className={`grid grid-cols-1 ${gridClass} gap-2`}>
        {options.map((o) => {
          const active = value === o.value;
          return (
            <button
              type="button"
              key={o.value}
              onClick={() => onChange(o.value)}
              data-testid={`${testidPrefix}-${o.value}`}
              className={`px-4 py-3 rounded-xl text-sm font-medium text-left transition-all ${
                active
                  ? "bg-[#FFCB05] text-[#040914] border border-[#FFCB05]"
                  : "bg-[#040914] border border-white/10 text-[#E5E7EB] hover:border-[#FFCB05]/40 hover:bg-white/5"
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
